import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import api from '../api/axios';
import useAuthStore from '../store/authStore';
import toast from 'react-hot-toast';

const ACOMPTE = 500;

export default function DoctorDetail() {
  const { id } = useParams();
  const { user, token } = useAuthStore();
  const navigate = useNavigate();
  const [doctor, setDoctor] = useState(null);
  const [slots, setSlots] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [type, setType] = useState('in_person');
  const [reason, setReason] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('mobile_money');
  const [loading, setLoading] = useState(true);
  const [booking, setBooking] = useState(false);
  const [agreed, setAgreed] = useState(false);

  useEffect(() => {
    api.get('/doctors/' + id).then(r => setDoctor(r.data)).finally(() => setLoading(false));
  }, [id]);

  useEffect(() => {
    if (id && selectedDate) {
      api.get('/doctors/' + id + '/slots?date=' + selectedDate)
        .then(r => setSlots(r.data.slots || []));
    }
  }, [id, selectedDate]);

  const handleBook = async () => {
    if (!token) { toast.error('Connectez-vous pour prendre un RDV'); navigate('/login'); return; }
    if (!selectedSlot) return toast.error('Sélectionnez un créneau');
    if (!agreed) return toast.error('Acceptez les CGV pour continuer');
    setBooking(true);
    try {
      await api.post('/appointments', {
        doctor_id: parseInt(id),
        scheduled_at: selectedDate + ' ' + selectedSlot + ':00',
        type, reason, payment_method: paymentMethod,
      });
      toast.success('Rendez-vous confirmé ! Acompte de 500 FCFA à régler.');
      navigate('/patient/appointments');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Erreur lors de la réservation');
    } finally {
      setBooking(false);
    }
  };

  if (loading) return (
    <div className="flex items-center justify-center min-h-screen">
      <div className="text-center"><div className="text-4xl animate-pulse mb-2">🏥</div><p className="text-gray-400">Chargement...</p></div>
    </div>
  );
  if (!doctor) return <div className="p-4 text-center text-gray-400">Médecin introuvable</div>;

  const dates = Array.from({length:7}, (_,i) => {
    const d = new Date(); d.setDate(d.getDate() + i);
    return { value: d.toISOString().split('T')[0], label: d.toLocaleDateString('fr-FR', {weekday:'short', day:'numeric', month:'short'}) };
  });

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white border-b px-4 py-3 flex items-center gap-3 sticky top-0 z-10">
        <Link to="/doctors" className="text-green-600">← Retour</Link>
        <h1 className="font-bold text-gray-800">Profil médecin</h1>
      </div>

      <div className="max-w-lg mx-auto p-4 space-y-4">
        {/* Infos médecin */}
        <div className="card">
          <div className="flex gap-4">
            <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center text-3xl">
              {doctor.user?.gender === 'female' ? '👩‍⚕️' : '👨‍⚕️'}
            </div>
            <div>
              <h2 className="font-bold text-lg">{doctor.user?.name}</h2>
              <p className="text-green-600 font-medium">{doctor.specialty?.name_fr}</p>
              <p className="text-sm text-gray-500">📍 {doctor.city} {doctor.clinic_name ? '• ' + doctor.clinic_name : ''}</p>
              <p className="text-sm text-gray-500">🏆 {doctor.experience_years} ans d'expérience</p>
            </div>
          </div>
          {doctor.bio && <p className="text-sm text-gray-600 mt-3 leading-relaxed">{doctor.bio}</p>}
          <div className="flex gap-4 mt-3 pt-3 border-t border-gray-100">
            <div className="text-center">
              <p className="font-bold text-green-700">{parseInt(doctor.consultation_fee).toLocaleString()}</p>
              <p className="text-xs text-gray-500">FCFA</p>
            </div>
            <div className="text-center">
              <p className="font-bold text-orange-600">{ACOMPTE}</p>
              <p className="text-xs text-gray-500">Acompte FCFA</p>
            </div>
            <div className="text-center">
              <p className="font-bold">{doctor.rating > 0 ? doctor.rating : '-'}</p>
              <p className="text-xs text-gray-500">⭐ Note</p>
            </div>
          </div>
          <div className="flex gap-2 mt-3 flex-wrap">
            {doctor.accepts_mobile_money && <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full">📱 Mobile Money</span>}
            {doctor.accepts_cash && <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full">💵 Espèces</span>}
            {doctor.teleconsultation_available && <span className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded-full">📹 Téléconsultation</span>}
            {doctor.home_visit_available && <span className="text-xs bg-orange-100 text-orange-700 px-2 py-1 rounded-full">🏠 Domicile</span>}
          </div>
        </div>

        {/* Acompte info */}
        <div className="bg-orange-50 border border-orange-200 rounded-2xl p-4">
          <div className="flex gap-3">
            <span className="text-2xl">💡</span>
            <div>
              <p className="font-semibold text-orange-800 text-sm">Acompte de réservation : 500 FCFA</p>
              <p className="text-xs text-orange-700 mt-1 leading-relaxed">
                Un acompte de 500 FCFA est requis pour confirmer votre rendez-vous.
                Il est déduit du montant total. Remboursable si annulation 24h avant.
              </p>
            </div>
          </div>
        </div>

        {/* Prise de RDV */}
        <div className="card space-y-4">
          <h3 className="font-bold text-gray-800">📅 Prendre un rendez-vous</h3>

          {/* Type */}
          <div>
            <label className="text-sm font-medium text-gray-700 block mb-2">Type de consultation</label>
            <div className="grid grid-cols-2 gap-2">
              {[['in_person','🏥 Cabinet'],['teleconsultation','📹 Vidéo']].map(([val,label]) => (
                doctor.teleconsultation_available || val === 'in_person' ? (
                  <button key={val} onClick={() => setType(val)}
                    className={`py-2 px-3 rounded-xl text-sm font-medium border-2 transition-all ${type===val ? 'border-green-500 bg-green-50 text-green-700' : 'border-gray-200 text-gray-600'}`}>
                    {label}
                  </button>
                ) : null
              ))}
            </div>
          </div>

          {/* Date */}
          <div>
            <label className="text-sm font-medium text-gray-700 block mb-2">Choisir une date</label>
            <div className="flex gap-2 overflow-x-auto pb-1">
              {dates.map(d => (
                <button key={d.value} onClick={() => { setSelectedDate(d.value); setSelectedSlot(null); }}
                  className={`flex-shrink-0 px-3 py-2 rounded-xl text-xs font-medium border-2 transition-all ${selectedDate===d.value ? 'border-green-500 bg-green-50 text-green-700' : 'border-gray-200 text-gray-600'}`}>
                  {d.label}
                </button>
              ))}
            </div>
          </div>

          {/* Créneaux */}
          <div>
            <label className="text-sm font-medium text-gray-700 block mb-2">Créneaux disponibles</label>
            {slots.length === 0 ? (
              <p className="text-sm text-gray-400 text-center py-3 bg-gray-50 rounded-xl">Aucun créneau disponible ce jour</p>
            ) : (
              <div className="grid grid-cols-4 gap-2">
                {slots.map(slot => (
                  <button key={slot.time} disabled={!slot.available}
                    onClick={() => setSelectedSlot(slot.time)}
                    className={`py-2 rounded-xl text-xs font-medium transition-all ${
                      !slot.available ? 'bg-gray-100 text-gray-400 cursor-not-allowed' :
                      selectedSlot === slot.time ? 'bg-green-600 text-white shadow-sm' : 'bg-green-50 text-green-700 hover:bg-green-100'
                    }`}>
                    {slot.time}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Motif */}
          <div>
            <label className="text-sm font-medium text-gray-700 block mb-1">Motif (optionnel)</label>
            <textarea className="input-field resize-none" rows={2}
              placeholder="Décrivez brièvement votre problème..."
              value={reason} onChange={e => setReason(e.target.value)} />
          </div>

          {/* Paiement acompte */}
          <div>
            <label className="text-sm font-medium text-gray-700 block mb-2">Paiement de l'acompte (500 FCFA)</label>
            <div className="grid grid-cols-2 gap-2">
              {[['mobile_money','📱 Mobile Money'],['cash','💵 Au cabinet']].map(([val,label]) => (
                <button key={val} onClick={() => setPaymentMethod(val)}
                  className={`py-2 px-3 rounded-xl text-sm font-medium border-2 transition-all ${paymentMethod===val ? 'border-green-500 bg-green-50 text-green-700' : 'border-gray-200 text-gray-600'}`}>
                  {label}
                </button>
              ))}
            </div>
          </div>

          {/* CGV */}
          <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-xl">
            <input type="checkbox" id="cgv" checked={agreed} onChange={e => setAgreed(e.target.checked)}
              className="mt-0.5 w-4 h-4 text-green-600 rounded" />
            <label htmlFor="cgv" className="text-xs text-gray-600 leading-relaxed cursor-pointer">
              J'accepte les <Link to="/cgv" className="text-green-600 underline" target="_blank">Conditions Générales de Vente</Link> et
              je comprends que l'acompte de 500 FCFA sera retenu en cas d'absence non justifiée.
            </label>
          </div>

          <button onClick={handleBook} disabled={booking || !selectedSlot || !agreed}
            className="btn-primary w-full py-3">
            {booking ? 'Réservation...' : `Confirmer le RDV — Acompte 500 FCFA`}
          </button>
        </div>
      </div>
    </div>
  );
}
